import BigLoader from "./common/BigLoader";
import Disabled from "./common/Disabled";
import UploadIcon from "@mui/icons-material/DriveFolderUpload";
import { Box, Typography } from "@mui/material";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import { useTheme } from "@mui/material/styles";
import Component from "components/Component";
import FormattedDatePicker from "components/common/FormattedDatePicker";
import * as labels from "config.labels";
import { captureDroppedEntries, processDroppedItems } from "fileUtils";
import { useCallback, useRef } from "react";
import { useDropzone } from "react-dropzone";
import { useTranslator } from "store/hooks/staticDataHooks";

const calculateAsAtDateHelperText = (date) => {
  if (date) {
    return `Recommended: ${new Date(date).toLocaleString("en-US", {
      timeZone: "UTC",
      day: "numeric",
      month: "long",
      year: "numeric",
    })}`;
  } else {
    return "";
  }
};

const FileUpload = ({
  onDrop,
  onDropWithPaths,
  disabled = false,
  size,
  asAtDateNotRequired,
  dropMessage,
  updateAsAtDate,
  asAtDate,
  recommendedAsAtDate,
  loading = false,
  options = null,
  title = null,
  showLoadingOverlay = false,
  loadingMessage = null,
}) => {
  const theme = useTheme();
  const translateLabel = useTranslator("uiLabels");
  const capturedEntriesRef = useRef(null);

  const handleNativeDrop = useCallback(
    (event) => {
      if (!onDropWithPaths) return;
      const items = event.dataTransfer?.items;
      if (!items || items.length === 0) return;
      const captured = captureDroppedEntries(items);
      if (captured.length > 0) {
        capturedEntriesRef.current = captured;
      }
    },
    [onDropWithPaths]
  );

  const handleDropzoneCallback = useCallback(
    async (acceptedFiles) => {
      if (onDropWithPaths && capturedEntriesRef.current) {
        const captured = capturedEntriesRef.current;
        capturedEntriesRef.current = null;
        const filesWithPaths = await processDroppedItems(captured);
        if (filesWithPaths.length > 0) {
          onDropWithPaths(filesWithPaths);
          return;
        }
      }
      if (onDrop) {
        onDrop(acceptedFiles);
      }
    },
    [onDrop, onDropWithPaths]
  );

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject,
    open: openFilePicker,
  } = useDropzone({
    onDrop: handleDropzoneCallback,
    disabled,
  });

  const rootProps = getRootProps();
  const mergedRootProps = {
    ...rootProps,
    onDrop: (event) => {
      handleNativeDrop(event);
      if (rootProps.onDrop) {
        rootProps.onDrop(event);
      }
    },
  };
  return (
    <Grid item xs={12} {...size}>
      <Component
        title={
          title ??
          translateLabel(labels.FILE_UPLOAD_TITLE, {
            default: "Upload Data",
          })
        }
        options={options}
      >
        <section style={{ position: "relative" }}>
          {loading ? (
            <BigLoader />
          ) : (
            <div
              {...mergedRootProps}
              className={`dropzone ${isDragActive ? "active" : ""} ${
                isDragAccept ? "accept" : ""
              } ${isDragReject ? "reject" : ""}`}
              style={{ marginBottom: 16 }}
              data-testid={"file-upload"}
            >
              <input {...getInputProps()} />
              <UploadIcon fontSize={"large"} />
              <p className={"message"}>
                {dropMessage ||
                  translateLabel(labels.FILE_UPLOAD_DROP_MESSAGE, {
                    default: "Drop an .xlsx, .csv, or .tsv file here to upload",
                  })}
              </p>
              <Divider style={{ width: "100%", marginBottom: 16 }}>
                {"OR"}
              </Divider>
              <Disabled ifReadOnly>
                <Button
                  type={"button"}
                  variant={"contained"}
                  disableElevation
                  color={"primary"}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!disabled) {
                      openFilePicker();
                    }
                  }}
                >
                  {translateLabel(labels.FILE_UPLOAD_BROWSE_FILES_BUTTON, {
                    default: "BROWSE FILES",
                  })}
                </Button>
              </Disabled>
            </div>
          )}

          {showLoadingOverlay && loadingMessage && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "rgba(255, 255, 255, 0.95)",
                borderRadius: "inherit",
                zIndex: 1000,
              }}
            >
              <Box
                sx={{
                  width: 60,
                  height: 60,
                  mb: 2,
                  border: "3px solid #f0f0f0",
                  borderTop: `3px solid ${theme.brand.accent[500]}`,
                  borderRadius: "50%",
                  animation: "spin 1s linear infinite",
                  "@keyframes spin": {
                    "0%": { transform: "rotate(0deg)" },
                    "100%": { transform: "rotate(360deg)" },
                  },
                }}
              />

              <Typography
                variant={"h6"}
                color={"primary"}
                sx={{ textAlign: "center", mb: 1 }}
              >
                {loadingMessage}
              </Typography>

              <Typography
                variant={"body2"}
                color={"text.secondary"}
                sx={{ textAlign: "center" }}
              >
                {"This may take a few moments"}
              </Typography>
            </Box>
          )}
        </section>
        {asAtDateNotRequired ? (
          ""
        ) : (
          <FormattedDatePicker
            label={translateLabel(labels.FILE_UPLOAD_AS_AT_DATE_LABEL, {
              default: "As At Date",
            })}
            onChangeDate={updateAsAtDate}
            value={asAtDate || recommendedAsAtDate || ""}
            helperText={calculateAsAtDateHelperText(recommendedAsAtDate)}
            fullWidth
            disabled={disabled || loading}
          />
        )}
      </Component>
    </Grid>
  );
};

export default FileUpload;
